import { type ReactNode } from 'react';
import { Select } from '@/components/ui/select';
import type {
  Creator,
  CreatorCategory,
  CreatorSex,
  CreatorType,
} from '@/types';
import { categoryLabel } from '@/utils/creator-helpers';

const CATEGORIES_BY_TYPE: Record<CreatorType, CreatorCategory[]> = {
  empresa: [1, 2, 3],
  persona: [1, 2, 3, 4],
};

export type AcceptFormState = {
  creator_type: CreatorType;
  category: CreatorCategory;
  sex: CreatorSex | '';
};

export function initAcceptForm(c: Creator): AcceptFormState {
  return { creator_type: c.creator_type, category: c.category, sex: c.sex };
}

export function toExtraData(f: AcceptFormState): Partial<Creator> {
  return { creator_type: f.creator_type, category: f.category, sex: f.sex };
}

function clamp(c: CreatorCategory, t: CreatorType): CreatorCategory {
  const max = t === 'empresa' ? 3 : 4;
  return c > max ? (max as CreatorCategory) : c;
}

function onTypeChange(f: AcceptFormState, t: CreatorType): AcceptFormState {
  const sex = t === 'empresa' ? '' : f.sex || 'H';
  return { creator_type: t, category: clamp(f.category, t), sex };
}

type Props = {
  form: AcceptFormState;
  setForm: (s: AcceptFormState) => void;
};

function TypeSelect({ form, setForm }: Props): ReactNode {
  return (
    <Select
      label="Tipo"
      value={form.creator_type}
      onChange={(e) => setForm(onTypeChange(form, e.target.value as CreatorType))}
    >
      <option value="persona">Persona</option>
      <option value="empresa">Empresa</option>
    </Select>
  );
}

function CategorySelect({ form, setForm }: Props): ReactNode {
  return (
    <Select
      label="Categoría"
      value={form.category}
      onChange={(e) =>
        setForm({ ...form, category: Number(e.target.value) as CreatorCategory })
      }
    >
      {CATEGORIES_BY_TYPE[form.creator_type].map((c) => (
        <option key={c} value={c}>
          {categoryLabel(form.creator_type, c)}
        </option>
      ))}
    </Select>
  );
}

function SexSelect({ form, setForm }: Props): ReactNode {
  if (form.creator_type === 'empresa') return null;
  return (
    <Select
      label="Sexo"
      value={form.sex || 'H'}
      onChange={(e) => setForm({ ...form, sex: e.target.value as CreatorSex })}
    >
      <option value="H">H</option>
      <option value="M">M</option>
    </Select>
  );
}

export function AcceptFormFields(p: Props): ReactNode {
  return (
    <div className="grid grid-cols-2 gap-sm">
      <TypeSelect form={p.form} setForm={p.setForm} />
      <CategorySelect form={p.form} setForm={p.setForm} />
      <SexSelect form={p.form} setForm={p.setForm} />
    </div>
  );
}
